import React, { useState } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { getAllProfiles, getCompressionProfile, compressImage } from '../../utils/imageCompression';
import { testScreenshotUpload } from '../../services/api';
import TestResults from './TestResults';

function ProfileTester() {
  const { settings, saveTestResults } = useSettings();
  const [testing, setTesting] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [testResults, setTestResults] = useState(settings.compression.testResults.results || []);
  const [error, setError] = useState('');

  /**
   * Generate a test image with patterns for compression testing
   */
  const generateTestImage = () => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 2560;
      canvas.height = 1440;
      const ctx = canvas.getContext('2d');

      // Fill with gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#1a2332');
      gradient.addColorStop(0.5, '#2d3741');
      gradient.addColorStop(1, '#1a2332');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add some patterns to simulate a screenshot
      ctx.fillStyle = '#00d4ff';
      ctx.font = 'bold 72px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('FPSTak Performance Test', canvas.width / 2, canvas.height / 2 - 100);

      ctx.fillStyle = '#e1e8ed';
      ctx.font = '36px Arial';
      ctx.fillText('Testing compression profiles...', canvas.width / 2, canvas.height / 2);

      // Add some colored boxes to test compression
      const colors = ['#ff6b6b', '#ffd93d', '#6bcf7f', '#4ecdc4', '#a8e6cf'];
      colors.forEach((color, i) => {
        ctx.fillStyle = color;
        ctx.fillRect(100 + i * 150, canvas.height - 300, 120, 200);
      });

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  };

  /**
   * Test a single profile
   */
  const testProfile = async (profile) => {
    const startTime = performance.now();
    const metrics = {
      profileId: profile.id,
      profileName: profile.name,
      resolution: `${profile.maxWidth}×${profile.maxHeight}`,
      quality: profile.quality,
    };

    try {
      // Generate test image
      const testImageBlob = await generateTestImage();
      metrics.originalSize = testImageBlob.size;

      // Compress
      const compressionStart = performance.now();
      const compressedBlob = await compressImage(testImageBlob, {
        format: profile.format,
        quality: profile.quality,
        maxWidth: profile.maxWidth,
        maxHeight: profile.maxHeight,
      });
      metrics.compressionTime = performance.now() - compressionStart;
      metrics.compressedSize = compressedBlob.size;
      metrics.compressionRatio = (metrics.originalSize / metrics.compressedSize).toFixed(2);
      metrics.reductionPercent = ((1 - metrics.compressedSize / metrics.originalSize) * 100).toFixed(1);

      // Upload to server
      const uploadStart = performance.now();
      const file = new File([compressedBlob], 'test-screenshot.jpg', { type: 'image/jpeg' });
      const serverResponse = await testScreenshotUpload(file, profile.id, metrics.originalSize);
      metrics.uploadTime = performance.now() - uploadStart;
      metrics.serverProcessingTime = serverResponse.performance?.serverProcessingTime || 0;

      // Calculate total time and network speed
      metrics.totalTime = performance.now() - startTime;
      metrics.networkSpeed = ((metrics.compressedSize * 8) / (metrics.uploadTime / 1000) / 1000000).toFixed(2); // Mbps

      metrics.success = true;
    } catch (err) {
      console.error(`[ProfileTester] Test failed for ${profile.name}:`, err);
      metrics.success = false;
      metrics.error = err.message;
    }

    return metrics;
  };

  /**
   * Test all profiles
   */
  const handleTestAllProfiles = async () => {
    setTesting(true);
    setError('');
    setTestResults([]);

    const profiles = getAllProfiles();
    const results = [];

    try {
      for (const profile of profiles) {
        setCurrentTest(profile.name);
        const result = await testProfile(profile);
        results.push(result);
        setTestResults([...results]); // Update UI after each test
      }

      // Save results to settings
      saveTestResults(results);
      setCurrentTest(null);

      console.log('[ProfileTester] All tests complete:', results);
    } catch (err) {
      console.error('[ProfileTester] Testing error:', err);
      setError('Testing failed: ' + err.message);
    } finally {
      setTesting(false);
      setCurrentTest(null);
    }
  };

  /**
   * Test single profile (active one)
   */
  const handleTestCurrentProfile = async () => {
    setTesting(true);
    setError('');

    try {
      const profileId = settings.compression.activeProfile;
      const customSettings = profileId === 'custom' ? settings.compression.customProfile : {};
      const profile = getCompressionProfile(profileId, customSettings);

      setCurrentTest(profile.name);
      const result = await testProfile(profile);
      setTestResults([result]);

      console.log('[ProfileTester] Single test complete:', result);
    } catch (err) {
      console.error('[ProfileTester] Testing error:', err);
      setError('Testing failed: ' + err.message);
    } finally {
      setTesting(false);
      setCurrentTest(null);
    }
  };

  return (
    <div className="profile-tester">
      <h4 className="compression-section-title">Performance Testing</h4>
      <p style={{ color: '#8899a6', fontSize: '14px', marginBottom: '16px' }}>
        Test compression profiles with real data to see how they perform on your connection
      </p>

      <div className="profile-actions">
        <button 
          className="btn btn-primary" 
          onClick={handleTestAllProfiles}
          disabled={testing}
        >
          {testing ? '⏳ Testing...' : '🧪 Test All Profiles'}
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={handleTestCurrentProfile}
          disabled={testing}
        >
          Test Current Profile
        </button>
      </div>

      {testing && currentTest && (
        <div className="test-progress">
          <div className="test-progress-item">
            <div className="test-progress-spinner"></div>
            <span className="test-progress-label">Testing {currentTest}...</span>
            <span className="test-progress-status">
              Compressing and uploading
            </span>
          </div>
        </div>
      )}

      {error && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: 'rgba(255, 107, 107, 0.1)', 
          border: '1px solid #ff6b6b',
          borderRadius: '6px',
          color: '#ff6b6b',
          fontSize: '14px',
          marginTop: '16px'
        }}>
          {error}
        </div>
      )}

      {testResults.length > 0 && <TestResults results={testResults} settings={settings} />}
    </div>
  );
}

export default ProfileTester;

